import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildLinearWorkflowRuntimeTransitions,
  formatWorkflowDate,
  getWorkflowStatusLabel,
  HISTORY_PAGE_SIZE,
  isWorkflowTransitionAllowedForActor,
  MAX_WORKFLOW_COMMENT_LENGTH,
  normalizeWorkflowRuntimeRole,
  normalizeWorkflowRuntimeStatus,
} from 'src/feature/Workflow/lib/runtime';
import ProjectsService from 'src/shared/api/projects/ProjectsService';
import { FormWorkflowHistoryItem, GetFormWorkflowRes } from 'src/shared/api/projects/types';
import { richTextToPlainText } from 'src/shared/lib/richText';
import { setError, setSuccess } from 'src/shared/lib/utils';
import {
  FormWorkflowSettings,
  FormWorkflowStatus,
  TableSettingsForm,
} from 'src/shared/store/projects/types';
import { Button, Modal, RichTextEditor, RichTextViewer } from 'src/shared/ui';

interface FormWorkflowPanelProps {
  tableSettings: TableSettingsForm;
  userRole?: string;
  userId?: number;
  onWorkflowUpdated: (workflow: GetFormWorkflowRes) => void;
}

export const FormWorkflowPanel: FC<FormWorkflowPanelProps> = ({
  tableSettings,
  userRole,
  userId,
  onWorkflowUpdated,
}) => {
  const [historyItems, setHistoryItems] = useState<FormWorkflowHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [commentHtml, setCommentHtml] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const workflowSettings = tableSettings.workflowSettings as FormWorkflowSettings | undefined;
  const workflowEnabled = Boolean(workflowSettings?.enabled);
  const normalizedRole = useMemo(() => normalizeWorkflowRuntimeRole(userRole), [userRole]);

  const statuses = useMemo(
    () =>
      [...(workflowSettings?.statuses || [])].sort(
        (a, b) => Number(a.order || 0) - Number(b.order || 0),
      ),
    [workflowSettings?.statuses],
  );

  const transitions = useMemo(() => {
    const configured = workflowSettings?.transitions || [];
    if (configured.length) {
      return configured;
    }
    return buildLinearWorkflowRuntimeTransitions(statuses);
  }, [statuses, workflowSettings?.transitions]);

  const statusMap = useMemo(
    () =>
      statuses.reduce<Record<string, FormWorkflowStatus>>((acc, status) => {
        const code = normalizeWorkflowRuntimeStatus(status.code);
        if (code) {
          acc[code] = { ...status, code };
        }
        return acc;
      }, {}),
    [statuses],
  );

  const currentStatusCode = useMemo(
    () =>
      normalizeWorkflowRuntimeStatus(
        tableSettings.workflowCurrentStatus || workflowSettings?.initialStatus || '',
      ),
    [tableSettings.workflowCurrentStatus, workflowSettings?.initialStatus],
  );

  const availableStatuses = useMemo(() => {
    if (!workflowEnabled || !workflowSettings?.manualTransitionEnabled) {
      return [];
    }

    const result = transitions
      .filter(transition => normalizeWorkflowRuntimeStatus(transition.from) === currentStatusCode)
      .filter(transition => isWorkflowTransitionAllowedForActor(transition, normalizedRole, userId))
      .map(transition => normalizeWorkflowRuntimeStatus(transition.to))
      .filter(Boolean);

    return result.filter((status, index) => result.indexOf(status) === index);
  }, [
    currentStatusCode,
    normalizedRole,
    transitions,
    userId,
    workflowEnabled,
    workflowSettings?.manualTransitionEnabled,
  ]);

  const commentPlainText = useMemo(() => richTextToPlainText(commentHtml), [commentHtml]);
  const isCommentRequired = Boolean(workflowSettings?.requireCommentOnTransition);

  const clearEditor = () => {
    setCommentHtml('');
  };

  const openCommentModal = () => {
    if (!selectedStatus) {
      setError('Выберите целевой статус');
      return;
    }

    setIsCommentModalOpen(true);
  };

  const loadHistory = useCallback(
    async (offset: number, append = false) => {
      setHistoryLoading(true);
      try {
        const { data } = await ProjectsService.getFormWorkflowHistory(tableSettings.id, {
          limit: HISTORY_PAGE_SIZE,
          offset,
        });
        const items = data?.items || [];
        setHistoryItems(prev => (append ? [...prev, ...items] : items));
        setHistoryTotal(data?.total || 0);
        setHistoryOffset(offset + items.length);
      } catch (error) {
        console.error('Failed to load form workflow history', error);
        setError('Не удалось загрузить историю бизнес-процесса');
      } finally {
        setHistoryLoading(false);
      }
    },
    [tableSettings.id],
  );

  const syncWorkflow = useCallback(async () => {
    const { data } = await ProjectsService.getFormWorkflow(tableSettings.id);
    onWorkflowUpdated(data);
    return data;
  }, [onWorkflowUpdated, tableSettings.id]);

  const handleTransition = async () => {
    if (!selectedStatus) {
      setError('Выберите целевой статус');
      return;
    }
    if (commentHtml.length > MAX_WORKFLOW_COMMENT_LENGTH) {
      setError('Комментарий к переходу превышает максимально допустимую длину');
      return;
    }
    if (isCommentRequired && !commentPlainText) {
      setError('Для перехода требуется комментарий');
      return;
    }

    setIsSubmitting(true);
    try {
      await ProjectsService.transitionFormWorkflow(tableSettings.id, {
        toStatus: selectedStatus,
        comment: commentHtml,
      });
      await syncWorkflow();
      if (isHistoryOpen) {
        await loadHistory(0, false);
      }
      clearEditor();
      setIsCommentModalOpen(false);
      setSuccess(`Статус изменён на ${getWorkflowStatusLabel(selectedStatus, statusMap)}`);
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Не удалось выполнить переход');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!workflowEnabled || !isHistoryOpen) {
      return;
    }

    void loadHistory(0, false);
  }, [currentStatusCode, isHistoryOpen, loadHistory, workflowEnabled]);

  useEffect(() => {
    if (!availableStatuses.length) {
      setSelectedStatus('');
      return;
    }

    setSelectedStatus(prev =>
      prev && availableStatuses.includes(normalizeWorkflowRuntimeStatus(prev)) ? prev : '',
    );
  }, [availableStatuses]);

  if (!workflowEnabled) {
    return null;
  }

  const noMoreHistory = historyOffset >= historyTotal;
  const currentStatusLabel = getWorkflowStatusLabel(currentStatusCode, statusMap);
  const historyContextLabel = tableSettings.formDescription?.trim() || tableSettings.formName;
  const hasTransitionControls = Boolean(
    workflowSettings?.manualTransitionEnabled && availableStatuses.length,
  );
  const transitionBlocked = !hasTransitionControls || !selectedStatus;

  return (
    <div className="mb-4 rounded border border-blue-400 bg-white p-4 shadow-white-xl dark:bg-violet-1100">
      <div className="mb-3 flex items-center">
        <div className="font-gothampro-500 text-base dark:text-white">
          Управление бизнес-процессом
        </div>
      </div>

      <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="min-w-0 flex-1 rounded border border-blue-400 p-3">
          <div className="text-extra-xs opacity-70 dark:text-white">Текущий статус</div>
          <div className="font-gothampro-500 mt-1 dark:text-white">{currentStatusLabel}</div>
        </div>

        {hasTransitionControls && (
          <>
            <div className="min-w-0 flex-1 rounded border border-blue-400 p-3">
              <div className="text-extra-xs opacity-70 dark:text-white">Целевой статус</div>
              <select
                className="font-gothampro-400 mt-2 w-full rounded border border-violet-400 bg-white p-2 text-xs text-black dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                value={selectedStatus}
                onChange={event => setSelectedStatus(event.target.value)}
                disabled={isSubmitting}
              >
                <option value="">Выберите статус</option>
                {availableStatuses.map(statusCode => (
                  <option key={statusCode} value={statusCode}>
                    {getWorkflowStatusLabel(statusCode, statusMap)}
                  </option>
                ))}
              </select>
            </div>

            <Button
              size="small"
              classNames="w-40 lg:w-36"
              variant="violetOutline"
              onClick={() => setIsHistoryOpen(true)}
              disabled={isSubmitting}
            >
              История переходов
            </Button>
            <Button
              size="small"
              classNames="w-40 lg:w-36"
              variant="green"
              onClick={openCommentModal}
              disabled={transitionBlocked || isSubmitting}
            >
              Выполнить переход
            </Button>
          </>
        )}
      </div>

      {isCommentModalOpen && (
        <Modal onClose={() => setIsCommentModalOpen(false)}>
          <div className="flex w-[44rem] max-w-[90dvw] flex-col">
            <div className="font-gothampro-500 mb-2 text-2xl text-green-100">
              Комментарий перехода
            </div>
            <div className="font-gothampro-400 mb-2 text-sm dark:text-white">
              {`Новый статус: ${getWorkflowStatusLabel(selectedStatus, statusMap)}`}
            </div>
            {isCommentRequired && (
              <div className="font-gothampro-400 mb-4 text-xs text-red-400">
                Комментарий обязателен для этого перехода
              </div>
            )}

            <RichTextEditor
              value={commentHtml}
              onChange={setCommentHtml}
              placeholder="Добавьте комментарий к переходу..."
              disabled={isSubmitting}
              maxLength={MAX_WORKFLOW_COMMENT_LENGTH}
              onError={setError}
            />
            <div className="mt-1 text-right text-extra-xs opacity-60 dark:text-white">
              {commentHtml.length}/{MAX_WORKFLOW_COMMENT_LENGTH}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <Button
                size="small"
                classNames="w-32"
                variant="greyLight"
                onClick={() => setIsCommentModalOpen(false)}
                disabled={isSubmitting}
              >
                Отмена
              </Button>
              <Button
                size="small"
                classNames="w-32"
                variant="green"
                onClick={() => void handleTransition()}
                disabled={isSubmitting}
              >
                Сохранить
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {isHistoryOpen && (
        <Modal onClose={() => setIsHistoryOpen(false)}>
          <div className="flex w-[56rem] max-w-[90dvw] flex-col">
            <div className="font-gothampro-500 mb-2 text-2xl text-green-100">История переходов</div>
            <div className="font-gothampro-400 mb-5 text-sm dark:text-white">
              {historyContextLabel}
            </div>

            <div className="mb-5 flex items-center justify-end gap-2">
              <Button
                size="small"
                classNames="w-32"
                variant="greyLight"
                onClick={() => void loadHistory(0, false)}
                disabled={historyLoading || isSubmitting}
              >
                Обновить
              </Button>
            </div>

            <div className="max-h-[32rem] overflow-y-auto rounded border border-blue-400 p-2">
              {historyLoading && (
                <div className="font-gothampro-400 p-2 text-sm opacity-70 dark:text-white">
                  Загрузка истории бизнес-процесса...
                </div>
              )}

              {!historyLoading && !historyItems.length && (
                <div className="font-gothampro-400 p-2 text-sm opacity-70 dark:text-white">
                  История переходов пока пуста
                </div>
              )}

              {!historyLoading &&
                historyItems.map(item => (
                  <div key={item.ID} className="mb-2 rounded border border-blue-400 p-3">
                    <div className="mb-1 text-extra-xs opacity-70 dark:text-white">
                      {formatWorkflowDate(item.ChangedAt)}
                    </div>
                    <div className="font-gothampro-500 text-sm dark:text-white">
                      {getWorkflowStatusLabel(item.FromStatus, statusMap)} -&gt;{' '}
                      {getWorkflowStatusLabel(item.ToStatus, statusMap)}
                    </div>
                    <div className="text-extra-xs opacity-70 dark:text-white">
                      {item.ChangedByEmail || `user #${item.ChangedByID}`} (
                      {item.ChangedByRole || '-'})
                    </div>
                    <div className="font-gothampro-400 mt-2 rounded bg-blue-rgba-100 p-2 text-xs text-black dark:text-white">
                      <RichTextViewer
                        value={item.Comment}
                        emptyText="Комментарий отсутствует"
                        className="break-words"
                      />
                    </div>
                  </div>
                ))}
            </div>

            {!historyLoading && historyItems.length > 0 && !noMoreHistory && (
              <div className="mt-2 flex justify-center">
                <Button
                  size="small"
                  classNames="w-48"
                  variant="violetOutline"
                  onClick={() => void loadHistory(historyOffset, true)}
                  disabled={isSubmitting}
                >
                  Загрузить ещё
                </Button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};
